import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import Layout from '@/components/common/Layout';

// Lazy loading all page components for automatic code splitting (P0 Blocker FE-2)
// This reduces the main bundle from 2.2MB to ~300KB
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const SecurityDashboardPage = lazy(() => import('@/pages/SecurityDashboardPage'));
const CalendarPage = lazy(() => import('@/pages/CalendarPage'));
const TaskPage = lazy(() => import('@/pages/TaskPage'));
const ProjectPage = lazy(() => import('@/pages/ProjectPage'));
const SecurityPage = lazy(() => import('@/pages/SecurityPage'));
const SecurityScannerPage = lazy(() => import('@/pages/SecurityScannerPage'));
const CompliancePage = lazy(() => import('@/pages/CompliancePage'));
const ComplianceSnapshotsPage = lazy(() => import('@/pages/ComplianceSnapshotsPage'));
const DependencyInsightsPage = lazy(() => import('@/pages/DependencyInsightsPage'));
const ExceptionWorkflowPage = lazy(() => import('@/pages/ExceptionWorkflowPage'));
const TeamPage = lazy(() => import('@/pages/TeamPage'));
const UserManagementPage = lazy(() => import('@/pages/UserManagementPage'));
const CMDBPage = lazy(() => import('@/pages/CMDBPage'));
const ReleaseManagementPage = lazy(() => import('@/pages/ReleaseManagementPage'));
const HealthPage = lazy(() => import('@/pages/HealthPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const SecretsManagementPage = lazy(() => import('@/pages/SecretsManagementPage'));
const SecretsDetectionConfigPage = lazy(() => import('@/pages/SecretsDetectionConfigPage'));
const CustomPatternManagerPage = lazy(() => import('@/pages/CustomPatternManagerPage'));
const IssueTrackerConfigPage = lazy(() => import('@/pages/IssueTrackerConfigPage'));
const HelpPage = lazy(() => import('@/pages/HelpPage'));
const GlossaryPage = lazy(() => import('@/pages/GlossaryPage'));
const UserJourneyPage = lazy(() => import('@/pages/UserJourneyPage'));
const SASTPage = lazy(() => import('@/pages/SASTPage'));

// Loading fallback component
const PageLoadingFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>
);

export const AppRoutes: React.FC = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/unauthorized" element={
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
                <p className="text-gray-600 mb-6">You don't have permission to access this resource.</p>
                <button onClick={() => window.history.back()} className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md">Go Back</button>
              </div>
            </div>
          } />

          {/* Protected routes with Layout */}
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<DashboardPage />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="security-dashboard" element={<SecurityDashboardPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="releases" element={<ReleaseManagementPage />} />
            <Route path="tasks" element={<TaskPage />} />
            <Route path="projects" element={<ProjectPage />} />
            <Route path="security" element={<SecurityPage />} />
            <Route path="scanner" element={<SecurityScannerPage />} />
            <Route path="sast" element={<SASTPage />} />
            <Route path="compliance" element={<CompliancePage />} />
            <Route path="compliance-snapshots" element={<ComplianceSnapshotsPage />} />
            <Route path="dependency-insights" element={<DependencyInsightsPage />} />
            <Route path="exceptions" element={<ExceptionWorkflowPage />} />
            
            {/* Secrets Detection Routes */}
            <Route path="secrets" element={<SecretsManagementPage />} />
            <Route path="secrets-detection/config" element={<SecretsDetectionConfigPage />} />
            <Route path="secrets-detection/patterns" element={<CustomPatternManagerPage />} />
            
            {/* Issue Tracker Integration Routes */}
            <Route path="integrations/issue-trackers" element={<IssueTrackerConfigPage />} />
            
            <Route path="reports" element={<ReportsPage />} />
            <Route path="team" element={<TeamPage />} />
            <Route path="users" element={<UserManagementPage />} />
            <Route path="cmdb" element={<CMDBPage />} />
            <Route path="health" element={<HealthPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="help" element={<HelpPage />} />
            <Route path="glossary" element={<GlossaryPage />} />
            <Route path="user-journey" element={<UserJourneyPage />} />
            
            {/* 404 Page - inside protected routes */}
            <Route path="*" element={
              <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <h1 className="text-2xl font-bold text-gray-900 mb-4">Page Not Found</h1>
                  <p className="text-gray-600 mb-6">The page you're looking for doesn't exist.</p>
                  <a href="/" className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md">Go Home</a>
                </div>
              </div>
            } />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};
